import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef} from 'react'
import styles from './RefPage.module.sass'
import {RefIntersection, RefKey} from '../../../../responses/refs/refs'
import Field from '../../../controls/Field/Field'
import Button, {ButtonStyle} from '../../../controls/Button/Button'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import {Column} from '../../../data-components/Table/Column'
import {Table} from '../../../data-components/Table/Table/Table'
import {refsActions} from '../../../../redux/pages/settings/refs'
import Spacer from '../../../etc/Spacer'
import {useNavigation, useRootDispatch, useRootSelector} from '../../../../utils/hooks'
import {Redirect, useParams, useRouteMatch} from 'react-router'
import {refsConfig} from './refs-config'
import {urlWithParams} from '../../../../http/utils'
import {ROUTES} from '../../../../http/routes'
import Helmet from 'react-helmet'
import Color from '../../../data-components/pickers/ColorPicker/Color'
import EditIcon from 'files/edit.svg'
import DeleteIcon from 'files/delete.svg'
import PlusIcon from 'files/plus.svg'
import SearchIcon from 'files/search.svg'
import {Checkbox} from '../../../controls/Checkbox/Checkbox'
import {fromBool, toBool} from '../../../../utils/utils'
import {Colors} from '../../../../styles'
import Tabs from '../../../navigation/Tabs/Tabs'
import {NavLink} from 'react-router-dom'

const RefPage: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {key: refKey} = useParams<{ key: string }>()
    const key = refKey as RefKey
    const reference = useMemo(() => refsConfig[key], [key])

    const {data, search, category} = useRootSelector(state => ({
        data: state.pages.settings.refs.data[key] || [],
        search: state.pages.settings.refs.search[key] || '',
        category: state.pages.settings.refs.category,
    }))

    const applyFilter = useCallback((newSearch?: string) => {
        dispatch(refsActions.request.init({
            key,
            search: (newSearch === undefined) ? search : newSearch,
            category,
        }))
    }, [category, dispatch, key, search])

    useEffect(() => {
        applyFilter()
    }, [applyFilter])

    const listRef = useRef(null)
    useNavigation(listRef)

    interface RouteConfig {
        key: string,
        name: string,
        category: string
    }

    const tabs: RouteConfig[] = [] // eslint-disable-line

    let useMenu = false
    //let tabSelect = undefined

    if (key == 'direction') {
        tabs.push({
            key: 'user',
            category: 'users',
            name: 'Сотрудники'
        }, {
            key: 'direction',
            category: 'users',
            name: 'Направления'
        }, {
            key: 'department',
            category: 'users',
            name: 'Отделы'
        })
        useMenu = true
        //tabSelect = tabs[1]
    }
    if (key == 'client' || key == 'brand') {
        tabs.push({
            key: 'client',
            category: 'client/brand',
            name: 'Клиенты'
        }, {
            key: 'brand',
            category: 'client/brand',
            name: 'Бренды',
        })
        useMenu = true
        //tabSelect = tabs[0]

    }

    const match = useRouteMatch<{ key: RefKey }>(ROUTES.SETTINGS_REF)
    const tabSelect = useMemo(() => tabs.find(tab => tab.key === match?.params.key), [match?.params?.key, tabs])

    if (!reference) {
        return <Redirect to={urlWithParams({key: 'country'})(ROUTES.SETTINGS_REF)}/>
    }


    return <div className={styles.refPage}>
        <Helmet><title>{reference.name}</title></Helmet>
        {
            useMenu && tabs && tabs.length > 0 && <div className={styles.headerMenu}><Tabs
                items={tabs}
                itemToKey={tab => tab.key}
                selected={tabSelect}
                renderer={tab => <NavLink
                    key={tab.key}
                    to={urlWithParams({key: tab.key})(ROUTES.SETTINGS_REF)}
                >
                    {tab.name}
                </NavLink>}
            /></div>
        }
        <div className={styles.header}>
            <Field
                type={'text'}
                placeholder={'Поиск'}
                rightItem={<SearchIcon/>}
                value={search}
                onChange={applyFilter}
            />
            <Spacer/>
            {
                reference.editable && !reference.update_only && <Button
                    buttonStyle={ButtonStyle.BORDERED}
                    color={Colors.blue}
                    onClick={() => dispatch(refsActions.startCreate(key))}
                >
                    <PlusIcon/> Добавить
                </Button>
            }
        </div>
        <InfiniteScroll
            ref={listRef}
            className={styles.body}
            onReach={() => dispatch(refsActions.request.more(key))}
        >
            <Table<RefIntersection>
                className={styles.table}
                data={data}
            >
                {

                    reference.columns.filter(column => column.hide !== true ? column : '').map(column =>
                        <Column<RefIntersection>
                            key={column.key}
                            headerRenderer={column.name}
                            cellRenderer={row => row[column.key]}
                        />)
                        .concat(key !== 'task-type' ? [] : [
                            <Column<RefIntersection>
                                key={'name'}
                                headerRenderer={'Название'}
                                cellRenderer={row => row.name}
                            />,
                            <Column<RefIntersection>
                                key={'color'}
                                headerRenderer={'Цвет / Тег'}
                                cellRenderer={row => <Color color={row.color}>{row.short_name}</Color>}
                            />,
                            <Column<RefIntersection>
                                key={'departments'}
                                headerRenderer={'Отделы'}
                                cellRenderer={row => {
                                    const text = []
                                    let i = 0
                                    while (i < row.departments.length) {
                                        text.push(row.departments[i].title)
                                        i++
                                    }
                                    return text.join(', ')
                                }
                                }
                            />,
                            <Column<RefIntersection>
                                key={'can_tendered'}
                                headerRenderer={'Тендерный'}
                                cellRenderer={row => <Checkbox
                                    className={styles.checkBox}
                                    value={fromBool(row.can_tendered)}
                                    onChange={value => dispatch(refsActions.update.request({
                                        key: 'task-type',
                                        old: row,
                                        changes: {
                                            id: row.id,
                                            can_tendered: toBool(value)
                                        }
                                    }))}
                                />}
                            />,
                            <Column<RefIntersection>
                                key={'keep_files'}
                                headerRenderer={'Сохранять файлы'}
                                cellRenderer={row => <Checkbox
                                    className={styles.checkBox}
                                    value={fromBool(row.keep_files)}
                                    onChange={value => dispatch(refsActions.update.request({
                                        key: 'task-type',
                                        old: row,
                                        changes: {
                                            id: row.id,
                                            keep_files: toBool(value)
                                        }
                                    }))}
                                />}
                            />])
                        .concat(!reference.editable ? [] : <Column<RefIntersection>
                            key={'row-actions'}
                            keyProp={'row-actions'}
                            cellRenderer={ref => <div className={styles.actions}>
                                <Button
                                    title={'Редактировать'}
                                    onClick={() => dispatch(refsActions.startEdit({key, ref}))}
                                >
                                    <EditIcon/>
                                </Button>
                                {!reference.update_only && <Button
                                    title={'Удалить'}
                                    onClick={() => dispatch(refsActions.startDelete({key, ref}))}
                                >
                                    <DeleteIcon/>
                                </Button>}
                            </div>}
                        />)
                }
            </Table>
        </InfiniteScroll>
    </div>
}

export default RefPage